import { useCallback, useState, useRef, Fragment, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  CssBaseline,
  AppBar,
  IconButton,
  MenuList,
  Button,
  Box,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { useLocation } from 'react-router';
import { isAfter } from 'date-fns';
import { useGetUsersData } from '../../hooks';
import {
  useSelector,
  uiSelectors,
  useDispatch,
  getAssignedServicesBasicList,
  systemSelectors,
} from '../../state';
import { generateUrl, paths } from '../../lib';
import { IconButtonLink, Dropdown, Notification, PageLoader } from '..';
import {
  AccountCircleIcon,
  DashboardIcon,
  ErrorIcon,
  TimerOutlinedIcon,
} from '../../assets';
import { TrackTimePage } from '../../pages/TrackTimePage';
import { GlobalWarehouseSelectModal } from '../common/modals/GlobalWarehouseSelectModal';

interface Props {
  handleLogout: () => any;
  children?: React.ReactNode;
}

export function MobileLayout({ handleLogout, children, ...props }: Props) {
  const { classes, css, cx } = useStyles();
  const { pathname } = useLocation();
  const { token, expiration, isCustomer, isManageAppointmentUser } =
    useGetUsersData();
  const dispatch = useDispatch();
  const hasValidToken = !!token && !isAfter(new Date(), new Date(expiration!));

  const assignedServices = useSelector(systemSelectors.assignedServices);
  const showTimer = useSelector(uiSelectors.showTimer);
  const hasMultipleWarehouses = useSelector(
    (state) => state.authPersist.data.hasMultipleWarehouses,
  );

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);

  const mobileAnchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (hasValidToken) {
      dispatch(getAssignedServicesBasicList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidToken]);

  const handleToggleMobileMenu = () => {
    setMobileMenuOpen((prevOpen) => !prevOpen);
  };

  const handleCloseMobileMenu = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (mobileAnchorRef.current?.contains(event.currentTarget as Node)) {
        return;
      }
      setMobileMenuOpen(false);
    },
    [],
  );

  const isTimerPage = pathname === `/${paths.TRACK_TIME}`;
  const isMobileHomePage = pathname === `/${paths.MOBILE_HOME}`;
  const shouldShowOpenTasksMessage = isMobileHomePage || isTimerPage;

  return (
    <Fragment>
      <CssBaseline />
      <div className={cx({ [classes.layout]: !isTimerPage })}>
        {children}
        {/* <div className={classes.offset} /> */}
      </div>
      <AppBar
        position='fixed'
        color='primary'
        className={classes.appBar}
        elevation={0}
      >
        <div className={classes.actionContainer}>
          <div className={classes.actions}>
            {!showTimer || isTimerPage ? (
              <IconButtonLink size='small' to={generateUrl(paths.TRACK_TIME)}>
                <TimerOutlinedIcon className={classes.icon} />
                {assignedServices?.length > 0 && (
                  <Box
                    position='absolute'
                    top={-8}
                    right={-8}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Tooltip
                      title='You have open tasks waiting to be worked on'
                      open={shouldShowOpenTasksMessage}
                      placement='right'
                    >
                      <ErrorIcon color='error' fontSize='small' />
                    </Tooltip>
                  </Box>
                )}
              </IconButtonLink>
            ) : (
              <span />
            )}
            {!isCustomer && !isManageAppointmentUser && (
              <IconButtonLink size='small' to={generateUrl(paths.MOBILE_HOME)}>
                <DashboardIcon className={classes.icon} />
              </IconButtonLink>
            )}
            <IconButton
              ref={mobileAnchorRef}
              onClick={handleToggleMobileMenu}
              // onBlur={!isMobile ? handleClose : undefined}
              size='small'
            >
              <AccountCircleIcon className={classes.icon} />
            </IconButton>
            <Dropdown
              ref={mobileAnchorRef}
              open={mobileMenuOpen}
              handleClose={handleCloseMobileMenu}
              paperProps={{
                elevation: 0,
                className: classes.mobilePaper,
              }}
              popperProps={{
                style: { zIndex: 1 },
                placement: 'bottom',
              }}
            >
              <MenuList
                autoFocusItem={mobileMenuOpen}
                dense
                className={css({
                  padding: 0,
                  gap: 2,
                  display: 'flex',
                  flexDirection: 'column',
                })}
              >
                {hasMultipleWarehouses && (
                  <MenuItem
                    dense
                    onClick={() => {
                      setWarehouseModalOpen(true);
                    }}
                  >
                    Update Warehouse
                  </MenuItem>
                )}
                <MenuItem
                  dense
                  sx={{
                    padding: 0,
                  }}
                >
                  <Button
                    id='log-out'
                    color='primary'
                    variant='contained'
                    onClick={handleLogout}
                    size='small'
                    fullWidth
                  >
                    log out
                  </Button>
                </MenuItem>
              </MenuList>
            </Dropdown>
            {warehouseModalOpen && (
              <GlobalWarehouseSelectModal
                onClose={() => setWarehouseModalOpen(false)}
                open={warehouseModalOpen}
              />
            )}
          </div>
        </div>
      </AppBar>
      {(showTimer || isTimerPage) && <TrackTimePage {...props} />}
      <Notification />
      <PageLoader />
    </Fragment>
  );
}

const useStyles = makeStyles({ name: { MobileLayout } })((theme) => ({
  layout: {
    height: 'calc(100vh - 64px)',
    maxHeight: 'calc(100vh - 64px)',
    overflowY: 'auto',
  },
  appBar: {
    borderRadius: '32px 32px 0 0',
    boxShadow: '0 2px 24px 0 #E5E7F5, 0 2px 40px 0 #F9FAFC',
    backgroundColor: '#FFFFFF',
    top: 'auto',
    bottom: 0,
    height: 64,
  },
  offset: {
    minHeight: 64,
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '80%',
    height: '100%',
  },

  icon: {
    fontSize: 22,
    color: '#43485B',
  },
  mobilePaper: {
    marginTop: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    // So we see the popover when it's empty.
    minWidth: 'initial',
    minHeight: 'initial',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    width: 'initial',
    // We disable the focus ring for mouse, touch and keyboard users.
    outline: 0,
    boxShadow: 'none',
    background: '#FFFFFF',
  },
}));
